import { Check, Clock, DollarSign, FileText, Phone } from "lucide-react";
import type { BaseComponentProps } from '../../types/puck';
import { Button } from "../ui/button";
import { SmartLink } from "../smart-link";
import { cn } from "../../lib/utils";

interface PricingInfoProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  priceRange?: string;
  priceNote?: string;
  ctaText: string;
  ctaUrl: string;
  features?: string[];
  showFreeEstimate: boolean;
  backgroundColor?: string;
}

const PricingInfo: React.FC<PricingInfoProps> = ({
  heading,
  subheading,
  priceRange,
  priceNote,
  ctaText,
  ctaUrl,
  features,
  showFreeEstimate = true,
  backgroundColor,
  id,
  className,
}) => (
  <section
    id={id}
    className={cn(
      "jobsite:py-16 jobsite:md:py-24",
      backgroundColor ? "" : "jobsite:bg-muted/50",
      className,
    )}
    style={backgroundColor ? { backgroundColor } : undefined}
  >
    <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
      <div className="jobsite:max-w-4xl jobsite:mx-auto">
        {/* Header */}
        <div className="jobsite:text-center jobsite:mb-10">
          <h2 className="jobsite:text-3xl jobsite:sm:text-4xl jobsite:mb-4 jobsite:font-heading jobsite:text-foreground">
            {heading}
          </h2>
          {subheading && (
            <p className="jobsite:text-lg jobsite:text-muted-foreground">{subheading}</p>
          )}
        </div>

        {/* Pricing Card */}
        <div
          className="jobsite:bg-background jobsite:border jobsite:border-border/70 jobsite:shadow-lg jobsite:overflow-hidden"
          style={{ borderRadius: "var(--jobsite-radius)" }}
        >
          <div className="jobsite:p-8 jobsite:md:p-10">
            <div className="jobsite:flex jobsite:flex-col jobsite:md:flex-row jobsite:items-center jobsite:justify-between jobsite:gap-8">
              {/* Price Display */}
              <div className="jobsite:text-center jobsite:md:text-left">
                {priceRange ? (
                  <>
                    <div className="jobsite:flex jobsite:items-baseline jobsite:justify-center jobsite:md:justify-start jobsite:gap-1">
                      <DollarSign className="jobsite:w-8 jobsite:h-8 jobsite:text-primary" />
                      <span className="jobsite:text-5xl jobsite:md:text-6xl jobsite:font-bold jobsite:text-foreground">
                        {priceRange}
                      </span>
                    </div>
                    {priceNote && (
                      <p className="jobsite:mt-2 jobsite:text-muted-foreground jobsite:text-base">
                        {priceNote}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="jobsite:space-y-2">
                    <span className="jobsite:text-3xl jobsite:md:text-4xl jobsite:font-bold jobsite:text-foreground">
                      Contact for Quote
                    </span>
                    <p className="jobsite:text-muted-foreground">
                      Pricing varies based on scope of work
                    </p>
                  </div>
                )}
              </div>

              {/* CTA */}
              <div className="jobsite:flex jobsite:flex-col jobsite:gap-3 jobsite:w-full jobsite:md:w-auto">
                <Button
                  asChild
                  size="lg"
                  className="jobsite:w-full jobsite:md:w-auto jobsite:text-lg jobsite:py-6 jobsite:px-8 jobsite:font-bold"
                >
                  <SmartLink to={ctaUrl}>{ctaText}</SmartLink>
                </Button>
                {showFreeEstimate && (
                  <p className="jobsite:text-sm jobsite:text-center jobsite:text-muted-foreground jobsite:flex jobsite:items-center jobsite:justify-center jobsite:gap-2">
                    <FileText className="jobsite:w-4 jobsite:h-4" />
                    Free, no-obligation estimates
                  </p>
                )}
              </div>
            </div>

            {/* Features */}
            {features && features.length > 0 && (
              <div className="jobsite:mt-8 jobsite:pt-8 jobsite:border-t jobsite:border-border/50">
                <p className="jobsite:text-sm jobsite:font-semibold jobsite:text-muted-foreground jobsite:mb-4 jobsite:uppercase jobsite:tracking-wide">
                  Included with this service:
                </p>
                <div className="jobsite:grid jobsite:grid-cols-1 jobsite:md:grid-cols-2 jobsite:gap-3">
                  {features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="jobsite:flex jobsite:items-center jobsite:gap-3 jobsite:text-card-foreground"
                    >
                      <div className="jobsite:flex jobsite:h-6 jobsite:w-6 jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-primary/10 jobsite:text-primary">
                        <Check className="jobsite:h-3.5 jobsite:w-3.5" strokeWidth={2.5} />
                      </div>
                      <span className="jobsite:text-sm jobsite:font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Bottom Banner */}
          <div className="jobsite:bg-primary/5 jobsite:px-8 jobsite:py-4 jobsite:flex jobsite:items-center jobsite:justify-center jobsite:gap-6 jobsite:flex-wrap">
            <div className="jobsite:flex jobsite:items-center jobsite:gap-2 jobsite:text-sm jobsite:text-muted-foreground">
              <Clock className="jobsite:w-4 jobsite:h-4 jobsite:text-primary" />
              Same-day service available
            </div>
            <div className="jobsite:flex jobsite:items-center jobsite:gap-2 jobsite:text-sm jobsite:text-muted-foreground">
              <Phone className="jobsite:w-4 jobsite:h-4 jobsite:text-primary" />
              Call for emergency pricing
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export type { PricingInfoProps };
export { PricingInfo };
